/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './Accordion.scss';
import { FlexColumn, FlexRow } from '../GridsAndLayouts';
import Button from '../Button';
import Typography from '../Typography';
import { CheckIcon, ChevronDownIcon } from '../Icons';

function findChildByType(children, type) {
  const items = React.Children.toArray(children);
  return items.find((child) => React.isValidElement(child) && child.type === type) ?? null;
}

function renderChildSlot(child) {
  if (child == null) return null;
  return React.isValidElement(child) ? child : null;
}

export function AccordionStepBadge({ status = 'pending', stepNumber, className = '' }) {
  const badgeClass = classNames(
    'ds-accordion__badge',
    status === 'completed' && 'ds-accordion__badge--completed',
    status === 'active' && 'ds-accordion__badge--active',
    status === 'pending' && 'ds-accordion__badge--pending',
    className
  );

  if (status === 'completed') {
    return (
      <FlexRow inline fullWidth={false} align="center" justify="center" className={badgeClass} aria-hidden>
        <FlexRow inline fullWidth={false} align="center" justify="center" className="ds-accordion__badge-icon">
          <CheckIcon size={16} strokeWidth={2} aria-hidden />
        </FlexRow>
      </FlexRow>
    );
  }

  return (
    <FlexRow inline fullWidth={false} align="center" justify="center" className={badgeClass} aria-hidden>
      <Typography component="span" variant="bodyMedium" fontWeight="semibold">
        {stepNumber}
      </Typography>
    </FlexRow>
  );
}

AccordionStepBadge.propTypes = {
  status: PropTypes.oneOf(['pending', 'active', 'completed']),
  stepNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
};

export function AccordionHeader({
  title = '',
  description = '',
  stepNumber,
  totalSteps,
  stepLabel,
  status = 'pending',
  expanded = false,
  completedLabel = 'Completed',
  showCompletedStatus = false,
  trailing,
  badge,
  className = '',
  id,
  panelId,
  onClick,
  disabled = false,
  ...rest
}) {
  const resolvedStepLabel =
    stepLabel ?? (stepNumber != null && totalSteps != null ? `Step ${stepNumber} of ${totalSteps}` : null);

  const resolvedBadge =
    badge ??
    (stepNumber != null || status === 'completed' ? (
      <AccordionStepBadge status={status} stepNumber={stepNumber} />
    ) : null);

  const showStatus = showCompletedStatus || status === 'completed';

  return (
    <Button
      id={id}
      type="button"
      variant="link"
      fullWidth
      justify="between"
      className={classNames('ds-accordion__header', className)}
      aria-expanded={expanded}
      aria-controls={panelId}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      {...rest}
    >
      {resolvedBadge}
      <FlexColumn align="start" fullWidth className="ds-accordion__content">
        <FlexRow wrap align="center" fullWidth className="ds-accordion__title-row">
          {title ? (
            <Typography component="span" className="ds-accordion__title">
              {title}
            </Typography>
          ) : null}
          {resolvedStepLabel ? (
            <Typography component="span" className="ds-accordion__step-label">
              {resolvedStepLabel}
            </Typography>
          ) : null}
        </FlexRow>
        {description ? (
          <Typography component="span" className="ds-accordion__description">
            {description}
          </Typography>
        ) : null}
      </FlexColumn>
      <FlexRow inline fullWidth={false} align="center" className="ds-accordion__trailing">
        {trailing}
        {showStatus ? (
          <FlexRow inline fullWidth={false} align="center" className="ds-accordion__status">
            <CheckIcon size={14} strokeWidth={2} aria-hidden />
            <Typography component="span" variant="bodySmall" fontWeight="semibold">
              {completedLabel}
            </Typography>
          </FlexRow>
        ) : null}
        <FlexRow
          inline
          fullWidth={false}
          align="center"
          justify="center"
          className={classNames('ds-accordion__chevron', expanded && 'ds-accordion__chevron--expanded')}
          aria-hidden
        >
          <ChevronDownIcon size={20} strokeWidth={1.42} />
        </FlexRow>
      </FlexRow>
    </Button>
  );
}

AccordionHeader.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  stepNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalSteps: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stepLabel: PropTypes.node,
  status: PropTypes.oneOf(['pending', 'active', 'completed']),
  expanded: PropTypes.bool,
  completedLabel: PropTypes.node,
  showCompletedStatus: PropTypes.bool,
  trailing: PropTypes.node,
  badge: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  panelId: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

export function AccordionBody({ children, className = '', ...rest }) {
  return (
    <FlexColumn align="stretch" fullWidth className={classNames('ds-accordion__body', className)} {...rest}>
      {children}
    </FlexColumn>
  );
}

AccordionBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export function AccordionFooter({ children, className = '', align = 'end', ...rest }) {
  return (
    <FlexRow
      align="center"
      justify={align === 'start' ? 'start' : 'end'}
      fullWidth
      className={classNames('ds-accordion__footer', align === 'start' && 'ds-accordion__footer--start', className)}
      {...rest}
    >
      {children}
    </FlexRow>
  );
}

AccordionFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  align: PropTypes.oneOf(['start', 'end']),
};

export function AccordionItem({
  children,
  id: idProp,
  className = '',
  status = 'pending',
  expanded = false,
  onToggle,
  accentColor,
  disabled = false,
  ...rest
}) {
  const autoId = useId();
  const itemId = idProp ?? autoId;
  const headerId = `${itemId}-header`;
  const panelId = `${itemId}-panel`;

  const headerChild = findChildByType(children, AccordionHeader);
  const bodyChild = findChildByType(children, AccordionBody);
  const footerChild = findChildByType(children, AccordionFooter);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    onToggle?.(!expanded);
  }, [disabled, expanded, onToggle]);

  const itemClass = classNames(
    'ds-accordion__item',
    expanded && 'ds-accordion__item--expanded',
    status === 'active' && 'ds-accordion__item--active',
    status === 'completed' && 'ds-accordion__item--completed',
    status === 'pending' && 'ds-accordion__item--pending',
    disabled && 'ds-accordion__item--disabled',
    className
  );

  const itemStyle = accentColor ? { '--ds-accordion-accent': accentColor } : undefined;

  const resolvedHeaderId = headerChild?.props?.id ?? headerId;

  const resolvedHeader = headerChild
    ? React.cloneElement(headerChild, {
        id: resolvedHeaderId,
        panelId: headerChild.props.panelId ?? panelId,
        expanded: headerChild.props.expanded ?? expanded,
        status: headerChild.props.status ?? status,
        onClick: headerChild.props.onClick ?? handleToggle,
        disabled: headerChild.props.disabled ?? disabled,
      })
    : null;

  return (
    <FlexColumn
      id={itemId}
      align="stretch"
      fullWidth
      className={itemClass}
      style={itemStyle}
      data-expanded={expanded || undefined}
      {...rest}
    >
      {resolvedHeader}
      {expanded ? (
        <FlexColumn id={panelId} role="region" aria-labelledby={resolvedHeaderId} align="stretch" fullWidth>
          {renderChildSlot(bodyChild)}
          {renderChildSlot(footerChild)}
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );
}

AccordionItem.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  className: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'active', 'completed']),
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  /** CSS color value; defaults to company theme via `--ds-theme-action-primary`. */
  accentColor: PropTypes.string,
  disabled: PropTypes.bool,
};

const Accordion = ({ children, className = '', gap, ...rest }) => {
  const style = gap != null ? { gap } : undefined;

  return (
    <FlexColumn className={classNames('ds-accordion', className)} align="stretch" fullWidth style={style} {...rest}>
      {children}
    </FlexColumn>
  );
};

Accordion.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Body = AccordionBody;
Accordion.Footer = AccordionFooter;
Accordion.StepBadge = AccordionStepBadge;

export default Accordion;
